import React, {useState} from 'react';

import {addToast, Button, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import type {ClassroomInvitationModelV1} from '@albert-io/models';
import useEnrollStudent from '../hooks/useEnrollStudent';
import useOnClassroomEnrollment from '../hooks/useOnClassroomEnrollment';

interface Props {
  classroomId: string | undefined;
  invitation: ClassroomInvitationModelV1;
}

export default function ClassroomInvitation({classroomId, invitation}: Props) {
  'use memo';

  const enrollStudent = useEnrollStudent();
  const onClassroomEnrollment = useOnClassroomEnrollment();

  const [isLoading, setIsLoading] = useState(false);

  async function onJoinClass() {
    setIsLoading(true);

    const relationship = {
      id: invitation.getClassroom().getId(),
      type: 'classroom_v1'
    };

    enrollStudent({
      onReject: () => {
        addToast({
          color: 'negative',
          message:
            'We were unable to complete your enrollment. Please verify your invitation with your teacher and try again.',
          title: 'Something went wrong'
        });

        setIsLoading(false);
      },
      onResolve: () => {
        onClassroomEnrollment(classroomId);

        setIsLoading(false);
      },
      relationship
    });
  }

  return (
    <li
      className='flex rounded-4xl background-positive py-x1-half pr-x1-half pl-x3 content-primary-inverse-static'
      data-testid='classroom-invitation'
      key={invitation.getId()}
    >
      <Icon className='mr-x3 self-center' color='primary-inverse' icon={['far', 'envelope-open']} />
      <div className='flex flex-1 flex-col items-start justify-center'>
        <Text size='s' color='inherit'>
          You've been invited to
        </Text>
        <Text size='l' bold color='inherit'>
          {invitation.getClassroom().getName()}
        </Text>
      </div>
      {isLoading ? (
        <LoadingSpinner
          bottomMarginLevel={0}
          className='self-center content-primary-inverse'
          size={2}
        />
      ) : (
        <Button className='self-center' color='blend-dark' onClick={onJoinClass} roundness='pill'>
          Join Class
        </Button>
      )}
    </li>
  );
}
